import React, { useMemo, useState } from 'react';
import { HiOutlineCheckCircle, HiOutlinePaperClip } from 'react-icons/hi';
import ACheckbox from '../../../../../../atoms/ACheckbox';
import SearchFilter from '../../../../../../atoms/SearchFilter';
import { ProviderTemplate } from '../../../../../../../types/communication';

interface ProviderTemplatePickerProps {
    selectedName: string | null;
    catalog: ProviderTemplate[];
    onPick: (name: string) => void;
    onCancel?: () => void;
}

const STATUS_DOT: Record<ProviderTemplate['status'], string> = {
    APPROVED: 'bg-emerald-500',
    PENDING: 'bg-amber-400',
    REJECTED: 'bg-red-500',
};

const STATUS_LABEL: Record<ProviderTemplate['status'], string> = {
    APPROVED: 'Aprovado',
    PENDING: 'Em análise',
    REJECTED: 'Reprovado',
};

const ProviderTemplatePicker: React.FC<ProviderTemplatePickerProps> = ({ selectedName, catalog, onPick, onCancel }) => {
    const [search, setSearch] = useState('');
    const [onlyApproved, setOnlyApproved] = useState(true);
    const [highlighted, setHighlighted] = useState<string | null>(selectedName);

    const filtered = useMemo(() => {
        const term = search.trim().toLocaleLowerCase('pt-BR');
        return catalog
            .filter((t) => !onlyApproved || t.status === 'APPROVED')
            .filter((t) => !term || `${t.name} ${t.body}`.toLocaleLowerCase('pt-BR').includes(term));
    }, [catalog, search, onlyApproved]);

    const preview = catalog.find((t) => t.name === highlighted);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
                <SearchFilter value={search} onChange={setSearch} placeholder="Buscar pelo nome ou texto..." className="flex-1 min-w-[220px]" />
                <label className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                    <ACheckbox checked={onlyApproved} onChange={setOnlyApproved} />
                    Só aprovados
                </label>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
                <div>
                    <div className="text-[11px] text-gray-400 mb-1">
                        {filtered.length} de {catalog.length} modelos
                    </div>
                    <ul className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-slate-700 divide-y divide-gray-100 dark:divide-slate-700">
                        {filtered.map((t) => {
                            const active = t.name === highlighted;
                            return (
                                <li key={t.name}>
                                    <button
                                        type="button"
                                        onClick={() => setHighlighted(t.name)}
                                        onDoubleClick={() => t.status === 'APPROVED' && onPick(t.name)}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-left ${
                                            active ? 'bg-teal-50 dark:bg-teal-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                        }`}
                                    >
                                        <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[t.status]}`} title={STATUS_LABEL[t.status]} />
                                        <span className="flex-1 min-w-0 font-mono text-xs text-gray-800 dark:text-gray-100 truncate">{t.name}</span>
                                        {t.header.type === 'DOCUMENT' && <HiOutlinePaperClip className="text-gray-400 shrink-0" size={12} />}
                                        {t.name === selectedName && <HiOutlineCheckCircle className="text-emerald-600 shrink-0" size={16} />}
                                    </button>
                                </li>
                            );
                        })}
                        {!filtered.length && <li className="px-3 py-4 text-sm text-gray-400">Nenhum modelo encontrado.</li>}
                    </ul>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 flex flex-col">
                    {preview ? (
                        <>
                            <div className="font-mono text-xs text-gray-800 dark:text-gray-100">{preview.name}</div>
                            <div className="text-[11px] text-gray-400 mt-0.5">
                                {STATUS_LABEL[preview.status]} · {preview.language}
                                {preview.header.type === 'DOCUMENT' ? ' · com anexo' : ''}
                            </div>
                            {preview.recategorized && (
                                <div className="mt-1 inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] text-amber-800 w-fit">
                                    Recategorizado: {preview.previousCategory} → {preview.category}
                                </div>
                            )}
                            <p className="text-xs text-gray-600 dark:text-gray-300 mt-2 whitespace-pre-line flex-1">{preview.body}</p>
                            <div className="flex justify-end gap-2 mt-3">
                                {onCancel && (
                                    <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-md text-xs bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-100">
                                        Cancelar
                                    </button>
                                )}
                                <button
                                    type="button"
                                    disabled={preview.status !== 'APPROVED'}
                                    title={preview.status !== 'APPROVED' ? 'Só modelos aprovados podem ser usados' : ''}
                                    onClick={() => onPick(preview.name)}
                                    className="px-3 py-1.5 rounded-md text-xs bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Usar este modelo
                                </button>
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-gray-400 m-auto">Selecione um modelo na lista para ver o texto.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProviderTemplatePicker;
